// ReportFilters.js
import React from 'react';
import { Card, DatePicker, Select, Button, Input, Switch, Space, Tooltip } from 'antd';
import { 
  SearchOutlined, ReloadOutlined, 
  QuestionCircleOutlined
} from '@ant-design/icons';
import DateRangeSelector from '../../DateRangeSelector'; // Adjust the import path as needed
import ClientOptionLabel, { buildClientSearchText, getClientSelectedLabel } from '../../common/ClientOptionLabel';

const { Option } = Select;

/**
 * Report Filters component for filtering sales report data
 * @param {Object} props - Component props
 * @param {string} props.dateViewMode - Current date view mode ('day', 'week', 'month')
 * @param {Date} props.currentDate - Currently selected date
 * @param {Array} props.dateRange - Selected date range [startDate, endDate]
 * @param {string} props.dateDisplayText - Formatted text representation of selected date range
 * @param {boolean} props.isCustomRange - Whether a custom date range is selected
 * @param {string|number|null} props.selectedCustomerId - Selected customer ID
 * @param {string} props.searchText - Search text for filtering
 * @param {string|null} props.orderStatusFilter - Order status filter value
 * @param {Array} props.customers - List of customers for dropdown
 * @param {boolean} props.excludeCleaningServices - Whether cleaning services are excluded
 * @param {boolean} props.showPricesWithoutVAT - Whether to show prices without VAT
 * @param {Function} props.handleDateNavigation - Handler for date navigation
 * @param {Function} props.handleDateViewModeChange - Handler for changing date view mode
 * @param {Function} props.handleCustomRangeChange - Handler for custom date range change
 * @param {Function} props.handleCustomerChange - Handler for customer selection
 * @param {Function} props.handleStatusFilterChange - Handler for status filter change
 * @param {Function} props.handleSearch - Handler for search text change
 * @param {Function} props.handleResetFilters - Handler for resetting all filters
 * @param {Function} props.handleToggleCleaningServices - Handler for toggling cleaning services exclusion
 * @param {Function} props.handleToggleVAT - Handler for toggling VAT display
 * @param {Function} props.fetchReportData - Handler for fetching report data
 * @param {boolean} props.isClient - Whether the current user is a client user
 */
const ReportFilters = ({
  dateViewMode,
  currentDate,
  dateRange,
  dateDisplayText,
  isCustomRange,
  selectedCustomerId,
  searchText,
  orderStatusFilter,
  customers,
  excludeCleaningServices,
  showPricesWithoutVAT,
  handleDateNavigation,
  handleDateViewModeChange,
  handleCustomRangeChange,
  handleCustomerChange,
  handleStatusFilterChange,
  handleSearch,
  handleResetFilters,
  handleToggleCleaningServices,
  handleToggleVAT,
  fetchReportData,
  onDataLoaded,
  isClient = false
}) => {
  // Handler for date range changes from DateRangeSelector
  const handleDateRangeChange = (newDateRange, mode, displayText, newDate) => {
    // Call appropriate handler based on the origin of the change
    if (mode === 'day' || mode === 'week' || mode === 'month') {
      handleDateViewModeChange(mode, newDate);
    } else if (mode === 'custom') {
      handleCustomRangeChange(newDateRange);
    }
  };

  return (
    <Card className="mb-6 shadow-md">
      <div className="flex flex-wrap justify-between items-center mb-4">
        {/* Date Range Selector */}
        <div className="mb-4 md:mb-0">
          <DateRangeSelector 
            dateViewMode={dateViewMode}
            currentDate={currentDate}
            dateDisplayText={dateDisplayText}
            isCustomRange={isCustomRange}
            onDateRangeChange={handleDateRangeChange}
            onCustomRangeChange={(isCustom, range) => {
              if (isCustom && range) {
                handleCustomRangeChange(range);
              } else {
                handleCustomRangeChange(false);
              }
            }}
            onDataLoaded={onDataLoaded}
          />
        </div>

        {/* Search and filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Only show customer selector if not a client user */}
          {!isClient && (
            <Select
              placeholder="Select Customer"
              style={{ width: 260 }}
              allowClear
              value={selectedCustomerId}
              onChange={handleCustomerChange}
              showSearch
              optionFilterProp="search"
              optionRender={(option) => <ClientOptionLabel client={option?.data?.client || option?.data} />}
              options={(customers || []).map((customer) => ({
                value: customer.id,
                label: getClientSelectedLabel(customer),
                client: customer,
                search: buildClientSearchText(customer),
              }))}
            />
          )}
          
          <Select
            placeholder="Order Status"
            style={{ width: 150 }}
            allowClear
            value={orderStatusFilter}
            onChange={handleStatusFilterChange}
          >
            <Option value="success">Successful</Option>
            <Option value="failed">Failed</Option>
          </Select>
          
          <Input
            placeholder="Search..."
            allowClear
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 200 }}
          />
          
          <Button
            icon={<ReloadOutlined />}
            onClick={handleResetFilters}
          >
            Reset
          </Button>
          
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchReportData}
            className="bg-golden-navy"
          >
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Only show toggle filters for admin users */}
      {!isClient && (
        <div className="flex flex-wrap items-center mt-3 gap-6">
          <Space>
            <Tooltip title="Exclude cleaning service items from totals and reports">
              <Switch 
                checked={excludeCleaningServices} 
                onChange={handleToggleCleaningServices} 
                size="small" 
              />
              <span className="ml-2 text-sm">Exclude Cleaning Services</span>
              <QuestionCircleOutlined className="ml-1 text-gray-400" />
            </Tooltip>
          </Space>
          
          <Space>
            <Tooltip title="Display prices without VAT and add VAT amount column">
              <Switch 
                checked={showPricesWithoutVAT} 
                onChange={handleToggleVAT} 
                size="small" 
              />
              <span className="ml-2 text-sm">Show Prices Without VAT</span>
              <QuestionCircleOutlined className="ml-1 text-gray-400" />
            </Tooltip>
          </Space>
        </div>
      )}
    </Card>
  );
};

export default ReportFilters;